import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Send, Mic, MessageCircleQuestion, Sparkles, Loader2,
  FileInput, Receipt, ShoppingCart, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/cn";
import {
  listChatMessages, sendChatMessage, dashboardKeys, type ChatMessage,
} from "@/lib/dashboard-api";
import { UploadCustomerPoModal } from "./upload-modals/UploadCustomerPoModal";
import { UploadVendorQuoteModal } from "./upload-modals/UploadVendorQuoteModal";
import { UploadBillModal } from "./upload-modals/UploadBillModal";
import { UploadGoodsReceiptModal } from "./upload-modals/UploadGoodsReceiptModal";

type ChatMode = "ask" | "ai_note";

const STORAGE_KEY_PREFIX = "dashboard-chat-mode:";

export function DashboardChatCard() {
  const { sessionId, user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [mode, setMode] = useState<ChatMode>("ask");
  useEffect(() => {
    if (!user?.id) return;
    const stored = window.localStorage.getItem(STORAGE_KEY_PREFIX + user.id);
    if (stored === "ask" || stored === "ai_note") setMode(stored);
  }, [user?.id]);
  function changeMode(m: ChatMode) {
    setMode(m);
    if (user?.id) window.localStorage.setItem(STORAGE_KEY_PREFIX + user.id, m);
  }

  const [input, setInput] = useState("");
  const [openModal, setOpenModal] = useState<"po" | "vq" | "bill" | "gr" | null>(null);

  const { data: history = [] } = useQuery<ChatMessage[]>({
    queryKey: [...dashboardKeys.chat(), sessionId],
    queryFn: () => listChatMessages(sessionId),
    enabled: !!sessionId,
  });

  const send = useMutation({
    mutationFn: (content: string) => sendChatMessage({ content, mode }, sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardKeys.chat() }),
    onError: (e: Error) => toast({ title: "Chat error", description: e.message, variant: "destructive" }),
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history.length, send.isPending]);

  function submit() {
    const v = input.trim();
    if (!v) return;
    setInput("");
    send.mutate(v);
  }

  return (
    <div className="rounded-lg border bg-card flex flex-col h-[420px]">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h2 className="text-sm font-semibold">What can I help you accomplish today?</h2>
      </div>
      {/* History */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2 text-sm">
        {history.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">
            Ask anything or review open to-do's. Power AI is in stub mode — answers are placeholders for now.
          </p>
        )}
        {history.map((m) => (
          <div key={m.id} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
            <div className={cn(
              "rounded-lg px-3 py-2 max-w-[80%] text-sm",
              m.role === "user"
                ? "bg-primary text-primary-foreground"
                : "bg-muted",
            )}>
              {m.content}
            </div>
          </div>
        ))}
        {send.isPending && (
          <div className="flex justify-start">
            <div className="rounded-lg px-3 py-2 bg-muted text-sm">
              <Loader2 className="h-3 w-3 animate-spin" />
            </div>
          </div>
        )}
      </div>

      {/* Input row */}
      <div className="border-t px-3 py-2 space-y-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
            <Plus className="h-4 w-4" />
          </Button>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), submit())}
            placeholder="Ask anything or review open to-do's..."
            className="h-8 text-sm"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={submit}
            disabled={!input.trim() || send.isPending}
          >
            <Send className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled>
            <Mic className="h-4 w-4" />
          </Button>
        </div>

        {/* Mode pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <ModePill
            active={mode === "ask"}
            icon={MessageCircleQuestion}
            label="Ask"
            onClick={() => changeMode("ask")}
          />
          <ModePill
            active={mode === "ai_note"}
            icon={Sparkles}
            label="AI Note"
            onClick={() => changeMode("ai_note")}
          />
          <PowerPill icon={FileInput} label="Customer PO" onClick={() => setOpenModal("po")} />
          <PowerPill icon={FileText} label="Vendor Quote" onClick={() => setOpenModal("vq")} />
          <PowerPill icon={Receipt} label="Vendor Invoice" onClick={() => setOpenModal("bill")} />
          <PowerPill icon={ShoppingCart} label="Goods Receipt" onClick={() => setOpenModal("gr")} />
        </div>
      </div>

      <UploadCustomerPoModal open={openModal === "po"} onOpenChange={(v) => !v && setOpenModal(null)} />
      <UploadVendorQuoteModal open={openModal === "vq"} onOpenChange={(v) => !v && setOpenModal(null)} />
      <UploadBillModal open={openModal === "bill"} onOpenChange={(v) => !v && setOpenModal(null)} />
      <UploadGoodsReceiptModal open={openModal === "gr"} onOpenChange={(v) => !v && setOpenModal(null)} />
    </div>
  );
}

function ModePill({
  active, icon: Icon, label, onClick,
}: { active: boolean; icon: React.ComponentType<{ className?: string }>; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors",
        active
          ? "border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400"
          : "border-muted text-muted-foreground hover:bg-muted/50",
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </button>
  );
}

function PowerPill({
  icon: Icon, label, onClick,
}: { icon: React.ComponentType<{ className?: string }>; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border border-purple-500/40 bg-purple-500/5 text-purple-600 dark:text-purple-300 hover:bg-purple-500/10 transition-colors"
    >
      <Icon className="h-3 w-3" />
      {label}
      <Badge variant="outline" className="text-[9px] py-0 px-1 border-purple-400/50 text-purple-500">
        Power
      </Badge>
    </button>
  );
}
